// Scoring weights. The model is FAIR-style: Risk = Likelihood × Impact.
//
//   Likelihood (loss-event frequency × vulnerability) composes:
//     1. USE_CASE_BASE_WEIGHTS  — per-(tech footprint, threat) relevance/frequency prior (0..1)
//     2. likelihood ENV_MODIFIERS (all keys EXCEPT IMPACT_ENV_KEYS)  — attack-surface / control-strength
//     3. AUTONOMY_MULTIPLIERS
//     4. maturityFactor (applied in scoring/index.ts) — controls reduce likelihood only
//
//   Impact (loss magnitude) composes:
//     1. THREAT_IMPACT          — inherent per-threat severity prior (0..1)
//     2. impact ENV_MODIFIERS (IMPACT_ENV_KEYS) — magnitude of loss; empty for v1 (see IMPACT_ENV_KEYS)
//   Impact is NOT reduced by maturity — a realized loss costs the same regardless of process maturity.
//
// Editing rules (also enforced by `pnpm validate:taxonomy`):
//   - all numeric values must be in [0, 1] for base weights + impact, [0, 4] for multipliers
//   - every referenced threatId must exist in modules/mappings/threats.ts
//   - every referenced useCaseId must exist in modules/taxonomy/usecases.ts
//   - add a row for a new tech-footprint domain here; weights default to 0 when absent
//
// Internal names (UseCaseId, USE_CASE_BASE_WEIGHTS, etc.) are kept from the
// upstream AI-risk engine even though this file now scores infrastructure
// technology domains against infrastructure threats, not AI use cases against
// AI threats — same mechanism, new content.

import type { EnvAnswers, UseCaseId } from "../shared/types";
import type { WeightsConfig } from "../content/types";

type ThreatWeights = Record<string, number>;

export const USE_CASE_BASE_WEIGHTS: Record<UseCaseId, ThreatWeights> = {
  nexus_dc: {
    eol_infra_exploitation: 0.85, unpatched_known_vuln: 0.6, machine_speed_cve: 0.6,
    flat_network_lateral_movement: 0.6, firewall_misconfig_exposure: 0.2,
    exposed_mgmt_interfaces: 0.5, multi_vendor_visibility_gaps: 0.4,
    cloud_onprem_hybrid_gap: 0.4, agentic_soc_alert_fatigue: 0.3,
    agent_prompt_injection_tool_abuse: 0.15, agent_identity_sprawl_shadow_agents: 0.15,
    supply_chain_oss_propagation: 0.2,
  },
  meraki: {
    eol_infra_exploitation: 0.35, unpatched_known_vuln: 0.4, machine_speed_cve: 0.4,
    rogue_wireless_access: 0.55, exposed_mgmt_interfaces: 0.4,
    flat_network_lateral_movement: 0.35, multi_vendor_visibility_gaps: 0.3,
    cloud_onprem_hybrid_gap: 0.3, supply_chain_oss_propagation: 0.15,
  },
  catalyst_sdwan: {
    sdwan_control_plane_exposure: 0.9, eol_infra_exploitation: 0.5,
    unpatched_known_vuln: 0.55, machine_speed_cve: 0.55,
    exposed_mgmt_interfaces: 0.55, flat_network_lateral_movement: 0.4,
    cloud_onprem_hybrid_gap: 0.45, multi_vendor_visibility_gaps: 0.35,
    supply_chain_oss_propagation: 0.15,
  },
  catalyst_switching: {
    eol_infra_exploitation: 0.8, unpatched_known_vuln: 0.6, machine_speed_cve: 0.55,
    flat_network_lateral_movement: 0.65, exposed_mgmt_interfaces: 0.5,
    rogue_wireless_access: 0.25, multi_vendor_visibility_gaps: 0.35,
    agentic_soc_alert_fatigue: 0.25, agent_prompt_injection_tool_abuse: 0.15,
    agent_identity_sprawl_shadow_agents: 0.15,
  },
  cisco_firewall: {
    firewall_misconfig_exposure: 0.9, exposed_mgmt_interfaces: 0.6,
    flat_network_lateral_movement: 0.55, eol_infra_exploitation: 0.5,
    unpatched_known_vuln: 0.55, machine_speed_cve: 0.55,
    regulated_data_exposure_infra: 0.45, multi_vendor_visibility_gaps: 0.35,
    agent_prompt_injection_tool_abuse: 0.15,
  },
  collaboration: {
    deepfake_vishing_social_engineering: 0.85, credential_phishing_token_theft: 0.75,
    unmanaged_byod_collab_exposure: 0.7, eol_infra_exploitation: 0.25,
    agentic_soc_alert_fatigue: 0.3, agent_identity_sprawl_shadow_agents: 0.15,
  },
  wifi: {
    rogue_wireless_access: 0.85, unmanaged_byod_collab_exposure: 0.45,
    exposed_mgmt_interfaces: 0.4, flat_network_lateral_movement: 0.4,
    eol_infra_exploitation: 0.35, unpatched_known_vuln: 0.35,
  },

  // Conservative mid-tier defaults across every threat the system knows.
  // Surfaces in the report as "non-Cisco infrastructure — generic risk
  // profile applied".
  other_non_cisco: {
    eol_infra_exploitation: 0.5, machine_speed_cve: 0.5, unpatched_known_vuln: 0.5,
    flat_network_lateral_movement: 0.45, credential_phishing_token_theft: 0.35,
    deepfake_vishing_social_engineering: 0.25, rogue_wireless_access: 0.3,
    sdwan_control_plane_exposure: 0.2, firewall_misconfig_exposure: 0.4,
    exposed_mgmt_interfaces: 0.45, agent_prompt_injection_tool_abuse: 0.2,
    agent_identity_sprawl_shadow_agents: 0.2, agentic_soc_alert_fatigue: 0.4,
    supply_chain_oss_propagation: 0.35, multi_vendor_visibility_gaps: 0.6,
    regulated_data_exposure_infra: 0.35, unmanaged_byod_collab_exposure: 0.3,
    cloud_onprem_hybrid_gap: 0.45,
  },
};

type EnvModifierEntry = {
  applies: string[];
  values: Record<string, number>;
};

// FAIR impact axis: inherent loss magnitude (0..1) per threat, independent of
// tech footprint, seeded by category severity and blast radius. High = a
// realized event causes severe, hard-to-reverse loss (regulated data,
// wide lateral movement, machine-speed compromise across the estate); low =
// mostly operational/coordination friction. Not modulated by any
// infrastructure-profile answer for v1 — see IMPACT_ENV_KEYS below.
export const THREAT_IMPACT: Record<string, number> = {
  eol_infra_exploitation: 0.85,
  machine_speed_cve: 0.8,
  unpatched_known_vuln: 0.7,
  flat_network_lateral_movement: 0.85,
  credential_phishing_token_theft: 0.75,
  deepfake_vishing_social_engineering: 0.65,
  rogue_wireless_access: 0.6,
  sdwan_control_plane_exposure: 0.85,
  firewall_misconfig_exposure: 0.75,
  exposed_mgmt_interfaces: 0.8,
  agent_prompt_injection_tool_abuse: 0.7,
  agent_identity_sprawl_shadow_agents: 0.65,
  agentic_soc_alert_fatigue: 0.7,
  supply_chain_oss_propagation: 0.75,
  multi_vendor_visibility_gaps: 0.55,
  regulated_data_exposure_infra: 0.9,
  unmanaged_byod_collab_exposure: 0.55,
  cloud_onprem_hybrid_gap: 0.6,
};

// FAIR routing: which ENV_MODIFIER keys describe *impact* (loss magnitude)
// rather than *likelihood* (attack surface / control strength). These are
// applied to the impact term; every other env key feeds likelihood.
//
// Intentionally empty for v1: the two dimensions that used to live here
// (regulated_industry, business_criticality_tier) were removed so the
// assessment scopes to the customer's whole environment generically, and no
// replacement impact-axis dimension was introduced. Impact is therefore a
// fixed per-threat severity prior (THREAT_IMPACT) for every organization —
// mirrors how DATA_SOURCE_FACTORS/GUARDRAIL_TYPE_FACTORS stay inert-by-design
// below rather than being deleted, so a future infrastructure-profile
// question can opt back into this axis without touching scoring/index.ts.
export const IMPACT_ENV_KEYS: string[] = [];

export const ENV_MODIFIERS: Partial<Record<keyof EnvAnswers, EnvModifierEntry>> = {
  network_complexity: {
    applies: [
      "flat_network_lateral_movement", "multi_vendor_visibility_gaps",
      "cloud_onprem_hybrid_gap", "agentic_soc_alert_fatigue", "exposed_mgmt_interfaces",
    ],
    values: { simple: 0.75, moderate: 1.0, complex: 1.2, highly_complex: 1.4 },
  },

  cloud_onprem_mix: {
    applies: ["cloud_onprem_hybrid_gap", "multi_vendor_visibility_gaps", "regulated_data_exposure_infra"],
    values: { all_on_prem: 0.85, hybrid: 1.2, mostly_cloud: 1.0, multi_cloud: 1.25 },
  },

  ai_agents_deployed: {
    applies: ["agent_prompt_injection_tool_abuse", "agent_identity_sprawl_shadow_agents", "agentic_soc_alert_fatigue"],
    values: { none: 0.3, pilot: 0.9, production: 1.4 },
  },
};

// multi_vendor_mix was removed as a question — every organization is now
// assumed to run a significant mix of Cisco and other-vendor infrastructure
// (the old "significant_multi_vendor" answer), since seams between vendors
// are exactly where these threats live. Applied unconditionally in
// likelihoodEnvMultiplierWith below, not per-org.
export const ASSUMED_MULTI_VENDOR_MULTIPLIER = 1.35;
export const MULTI_VENDOR_THREATS: string[] = [
  "multi_vendor_visibility_gaps",
  "cloud_onprem_hybrid_gap",
  "agentic_soc_alert_fatigue",
];

// Autonomy multipliers keyed by the 1..5 SOC/incident-response autonomy
// ladder (see ./autonomy.ts). Scales the threats an autonomous, machine-speed
// response posture most directly affects — both by containing them faster
// (dampening effect folds into the multiplier direction below) and by
// expanding the attack surface an under-governed autonomous response process
// itself introduces.
export const AUTONOMY_THREATS: string[] = [
  "agent_prompt_injection_tool_abuse",
  "agent_identity_sprawl_shadow_agents",
  "agentic_soc_alert_fatigue",
];

// Multi-select data/tool-source and guardrail-type amplifiers from the
// upstream AI-risk engine. No infrastructure-profile question in this app is
// multi-select in a way that populates `data_sources` or `guardrails_types`,
// so both tables are empty for v1 — the mechanism is retained (rather than
// deleted) so a future infrastructure-profile question can opt back into it
// without touching scoring/index.ts or weights.ts's function bodies.
export const DATA_SOURCE_FACTORS: Record<string, { threats: string[]; factor: number }> = {};
export const GUARDRAIL_TYPE_FACTORS: Record<string, { threats: string[]; factor: number }> = {};

export const AUTONOMY_MULTIPLIERS: Record<number, number> = {
  1: 0.5,
  2: 0.8,
  3: 1.0,
  4: 1.3,
  5: 1.6,
};

// Scoring tunables — defaults applied when a WeightsConfig omits them. Values
// carried over unchanged from the upstream engine's corpus-validated
// defaults; re-tune against `pnpm score:scenarios` if this app's own fixture
// scenarios suggest otherwise.
export const DEFAULT_RESIDUAL_FLOOR = 0.15;
export const DEFAULT_GUARDRAIL_FLOOR = 0.35;
export const DEFAULT_OVERALL_PEAK_WEIGHT = 0.5;

// Soft cap replacing a hard clamp at 1.0. Stays linear (identity) below 0.8, then
// asymptotically approaches 1.0 — so distinct high-risk profiles keep their ordering
// instead of all collapsing onto exactly 1.0.
export function softCap01(x: number): number {
  if (x <= 0) return 0;
  if (x <= 0.8) return x;
  return 0.8 + 0.2 * (1 - Math.exp(-(x - 0.8) / 0.2));
}

// The built-in weights, assembled as a plain-data config. Runtime overrides
// (admin GUI) produce a merged WeightsConfig that scoring consumes instead.
export const DEFAULT_WEIGHTS_CONFIG: WeightsConfig = {
  base: USE_CASE_BASE_WEIGHTS,
  envModifiers: ENV_MODIFIERS as Record<string, EnvModifierEntry>,
  autonomyMultipliers: AUTONOMY_MULTIPLIERS,
  autonomyThreats: AUTONOMY_THREATS,
  guardrailTypeFactors: GUARDRAIL_TYPE_FACTORS,
  dataSourceFactors: DATA_SOURCE_FACTORS,
  residualFloor: DEFAULT_RESIDUAL_FLOOR,
  guardrailFloor: DEFAULT_GUARDRAIL_FLOOR,
  overallPeakWeight: DEFAULT_OVERALL_PEAK_WEIGHT,
  threatImpact: THREAT_IMPACT,
  impactEnvKeys: IMPACT_ENV_KEYS,
};

// ---- Config-driven scoring primitives (used when overrides are active) ----

export function baseWeightWith(
  cfg: WeightsConfig,
  useCase: UseCaseId,
  threatId: string,
): number {
  return cfg.base[useCase]?.[threatId] ?? 0;
}

export function autonomyMultiplierWith(
  cfg: WeightsConfig,
  level: number,
  threatId: string,
): number {
  if (!cfg.autonomyThreats.includes(threatId)) return 1;
  return cfg.autonomyMultipliers[level] ?? 1;
}

export function guardrailTypesMultiplierWith(
  cfg: WeightsConfig,
  env: EnvAnswers,
  threatId: string,
): number {
  const raw = (env as Record<string, string | undefined>).guardrails_types;
  if (!raw) return 1;
  const types = raw.split(",").map((s) => s.trim()).filter(Boolean);
  let m = 1;
  for (const t of types) {
    const entry = cfg.guardrailTypeFactors[t];
    if (entry && entry.threats.includes(threatId)) m *= entry.factor;
  }
  return m;
}

// Multi-select data/tool sources, comma-joined in env, each amplifying the
// threats it exposes. Multiplicative like the guardrail types, and
// deliberately *not* floored: connecting a second external system really
// does add independent surface. Inert for v1 (see DATA_SOURCE_FACTORS above).
export function dataSourcesMultiplierWith(
  cfg: WeightsConfig,
  env: EnvAnswers,
  threatId: string,
): number {
  const raw = (env as Record<string, string | undefined>).data_sources;
  const factors = cfg.dataSourceFactors ?? DATA_SOURCE_FACTORS;
  if (!raw) return 1;
  const sources = raw.split(",").map((s) => s.trim()).filter(Boolean);
  let m = 1;
  for (const s of sources) {
    const entry = factors[s];
    if (entry && entry.threats.includes(threatId)) m *= entry.factor;
  }
  return m;
}

function impactKeySet(cfg: WeightsConfig): Set<string> {  return new Set(cfg.impactEnvKeys ?? IMPACT_ENV_KEYS);
}

// Likelihood env multiplier: every env dimension EXCEPT the impact axes, with
// the guardrail bucket (guardrails_maturity ladder × guardrail types) folded
// together and floored so layered guardrails can't double-count the same
// protection into an implausibly low exposure. Inert for v1 since no
// infrastructure-profile dimension is named "guardrails_maturity".
export function likelihoodEnvMultiplierWith(
  cfg: WeightsConfig,
  env: EnvAnswers,
  threatId: string,
): number {
  const impactKeys = impactKeySet(cfg);
  let nonGuardrail = 1;
  let guardrail = 1;
  for (const dimKey of Object.keys(cfg.envModifiers)) {
    if (impactKeys.has(dimKey)) continue;
    const entry = cfg.envModifiers[dimKey];
    if (!entry || !entry.applies.includes(threatId)) continue;
    const val = (env as Record<string, string | undefined>)[dimKey];
    if (val && val in entry.values) {
      if (dimKey === "guardrails_maturity") guardrail *= entry.values[val];
      else nonGuardrail *= entry.values[val];
    }
  }
  nonGuardrail *= dataSourcesMultiplierWith(cfg, env, threatId);
  if (MULTI_VENDOR_THREATS.includes(threatId)) nonGuardrail *= ASSUMED_MULTI_VENDOR_MULTIPLIER;
  guardrail *= guardrailTypesMultiplierWith(cfg, env, threatId);  const floor = cfg.guardrailFloor ?? DEFAULT_GUARDRAIL_FLOOR;
  // Only floor the reductive direction; an amplifying ladder value (e.g. "none"
  // = 1.4) is left untouched.
  const flooredGuardrail = guardrail < 1 ? Math.max(floor, guardrail) : guardrail;
  return nonGuardrail * flooredGuardrail;
}

// Impact env multiplier: only the impact axes (IMPACT_ENV_KEYS — empty for v1).
export function impactEnvMultiplierWith(
  cfg: WeightsConfig,
  env: EnvAnswers,
  threatId: string,
): number {
  let m = 1;
  for (const dimKey of impactKeySet(cfg)) {
    const entry = cfg.envModifiers[dimKey];
    if (!entry || !entry.applies.includes(threatId)) continue;
    const val = (env as Record<string, string | undefined>)[dimKey];
    if (val && val in entry.values) m *= entry.values[val];
  }
  return m;
}

// Legacy total env multiplier (likelihood × impact axes). Retained for the
// back-compat wrappers; the FAIR scoring path uses the split functions above.
export function envMultiplierWith(
  cfg: WeightsConfig,
  env: EnvAnswers,
  threatId: string,
): number {
  return (
    likelihoodEnvMultiplierWith(cfg, env, threatId) *
    impactEnvMultiplierWith(cfg, env, threatId)
  );
}

// FAIR inherent impact prior for a threat (0..1); defaults to 0.5 when absent.
export function threatImpactWith(cfg: WeightsConfig, threatId: string): number {
  return cfg.threatImpact?.[threatId] ?? 0.5;
}

// FAIR Likelihood term (pre-maturity): base relevance × likelihood env axes ×
// autonomy, soft-capped. Maturity is applied downstream in scoring/index.ts.
export function profileLikelihoodWith(
  cfg: WeightsConfig,
  useCase: UseCaseId,
  env: EnvAnswers,
  threatId: string,
  autonomyLevel = 3,
): number {
  const base = baseWeightWith(cfg, useCase, threatId);
  if (base === 0) return 0;
  return softCap01(
    base *
      likelihoodEnvMultiplierWith(cfg, env, threatId) *
      autonomyMultiplierWith(cfg, autonomyLevel, threatId),
  );
}

// FAIR Impact term (0..1): inherent per-threat severity × impact env axes.
// Not reduced by maturity. Soft-capped (not hard-clamped) so high-impact
// profiles stay ordered instead of all collapsing onto exactly 1.0.
export function profileImpactWith(
  cfg: WeightsConfig,
  env: EnvAnswers,
  threatId: string,
): number {
  return softCap01(
    threatImpactWith(cfg, threatId) * impactEnvMultiplierWith(cfg, env, threatId),
  );
}

export function profileBaseExposureWith(
  cfg: WeightsConfig,
  useCase: UseCaseId,
  env: EnvAnswers,
  threatId: string,
  autonomyLevel = 3,
): number {
  const base = baseWeightWith(cfg, useCase, threatId);
  if (base === 0) return 0;
  return softCap01(
    base *
      envMultiplierWith(cfg, env, threatId) *
      autonomyMultiplierWith(cfg, autonomyLevel, threatId),
  );
}

export function relevantControlIdsWith(
  cfg: WeightsConfig,
  useCase: UseCaseId,
  threats: { primaryControlIds: string[]; secondaryControlIds: string[]; id: string }[],
): Set<string> {
  const ids = new Set<string>();
  const weights = cfg.base[useCase] ?? {};
  for (const t of threats) {
    if ((weights[t.id] ?? 0) > 0) {
      t.primaryControlIds.forEach((c) => ids.add(c));
      t.secondaryControlIds.forEach((c) => ids.add(c));
    }
  }
  return ids;
}

// ---- Back-compat wrappers: default-bound to the built-in config ----

export function autonomyMultiplier(level: number, threatId: string): number {
  return autonomyMultiplierWith(DEFAULT_WEIGHTS_CONFIG, level, threatId);
}

export function guardrailTypesMultiplier(env: EnvAnswers, threatId: string): number {
  return guardrailTypesMultiplierWith(DEFAULT_WEIGHTS_CONFIG, env, threatId);
}

export function baseWeight(useCase: UseCaseId, threatId: string): number {
  return baseWeightWith(DEFAULT_WEIGHTS_CONFIG, useCase, threatId);
}

export function envMultiplier(env: EnvAnswers, threatId: string): number {
  return envMultiplierWith(DEFAULT_WEIGHTS_CONFIG, env, threatId);
}

export function profileBaseExposure(
  useCase: UseCaseId,
  env: EnvAnswers,
  threatId: string,
  autonomyLevel = 3,
): number {
  return profileBaseExposureWith(
    DEFAULT_WEIGHTS_CONFIG,
    useCase,
    env,
    threatId,
    autonomyLevel,
  );
}

export function profileLikelihood(
  useCase: UseCaseId,
  env: EnvAnswers,
  threatId: string,
  autonomyLevel = 3,
): number {
  return profileLikelihoodWith(
    DEFAULT_WEIGHTS_CONFIG,
    useCase,
    env,
    threatId,
    autonomyLevel,
  );
}

export function profileImpact(env: EnvAnswers, threatId: string): number {
  return profileImpactWith(DEFAULT_WEIGHTS_CONFIG, env, threatId);
}

// Adaptive: which controls' maturity questions are relevant for this
// profile? A control is relevant if any threat referencing it has non-zero
// base weight for the selected tech-footprint domain.
export function relevantControlIds(
  useCase: UseCaseId,
  threats: { primaryControlIds: string[]; secondaryControlIds: string[]; id: string }[],
): Set<string> {
  return relevantControlIdsWith(DEFAULT_WEIGHTS_CONFIG, useCase, threats);
}
